/**
 * API Client Utility
 *
 * Centralized HTTP client for making API requests to the backend.
 * Supports GET, POST, PUT, PATCH, and DELETE methods with automatic
 * JSON serialization, error handling, and authentication token injection.
 */

import type { ApiResponse } from "@/types/api";

/**
 * Get the API base URL from environment variables
 */
const getApiBaseUrl = (): string => {
  const baseUrl = import.meta.env.VITE_API_BASE_URL;
  if (!baseUrl) {
    console.error(
      "VITE_API_BASE_URL is not defined in environment variables"
    );
    return "http://localhost:5222"; // Fallback for local development
  }
  return baseUrl;
};

/**
 * Request configuration options
 */
interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean>;
  requiresAuth?: boolean;
  /** Abort after this many milliseconds. Defaults to 10 s, or 2 min for file uploads. */
  timeoutMs?: number;
}

const DEFAULT_TIMEOUT_MS = 10_000;
const UPLOAD_TIMEOUT_MS = 120_000;

/**
 * Custom error class for API errors
 */
export class ApiRequestError extends Error {
  status: number;
  errors?: Record<string, string[]>;

  constructor(
    message: string,
    status: number,
    errors?: Record<string, string[]>
  ) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
    this.errors = errors;
  }
}

/**
 * Build query string from params object
 */
const buildQueryString = (
  params?: Record<string, string | number | boolean>
): string => {
  if (!params || Object.keys(params).length === 0) return "";

  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    searchParams.append(key, String(value));
  });

  return `?${searchParams.toString()}`;
};

/**
 * Auth endpoints never trigger a refresh-and-retry: they either establish the session
 * themselves or a 401 from them is the definitive answer.
 */
const NO_REFRESH_ENDPOINTS = [
  "/auth/login",
  "/auth/register",
  "/auth/refresh-token",
  "/auth/logout",
  "/auth/me",
];

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Rotates the session using the HTTP-only refresh-token cookie. Concurrent callers share one
 * request, so a burst of 401s results in a single refresh (the server revokes the old refresh
 * token on use, so parallel refreshes would race each other).
 */
function refreshSession(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = fetch(`${getApiBaseUrl()}/auth/refresh-token`, {
      method: "POST",
      credentials: "include",
    })
      .then((response) => response.ok)
      .catch(() => false)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

/**
 * Core request function that handles all HTTP methods. Access tokens are short-lived, so a 401
 * triggers one session refresh and a single retry of the original request.
 */
async function request<T = unknown>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  try {
    return await performRequest<T>(endpoint, options);
  } catch (error) {
    const canRefresh =
      error instanceof ApiRequestError &&
      error.status === 401 &&
      !NO_REFRESH_ENDPOINTS.some((path) => endpoint.startsWith(path));

    if (canRefresh && (await refreshSession())) {
      return performRequest<T>(endpoint, options);
    }
    throw error;
  }
}

async function performRequest<T = unknown>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { params, headers = {}, timeoutMs, ...fetchOptions } = options;

  const baseUrl = getApiBaseUrl();
  const queryString = buildQueryString(params);
  const url = `${baseUrl}${endpoint}${queryString}`;

  // Build headers
  const requestHeaders: Record<string, string> = {
    ...(headers as Record<string, string>),
  };

  // Only set Content-Type to JSON if not already set and body is not FormData
  if (!requestHeaders["Content-Type"] && !(fetchOptions.body instanceof FormData)) {
    requestHeaders["Content-Type"] = "application/json";
  }

  // Remove Content-Type if body is FormData (browser will set it with boundary)
  if (fetchOptions.body instanceof FormData) {
    delete requestHeaders["Content-Type"];
  }

  // Auth tokens are in HTTP-only cookies, no need to set Authorization header
  // The cookies will be sent automatically with credentials: 'include'

  // Create abort controller with timeout
  const controller = new AbortController();
  const timeout =
    timeoutMs ?? (fetchOptions.body instanceof FormData ? UPLOAD_TIMEOUT_MS : DEFAULT_TIMEOUT_MS);
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      headers: requestHeaders,
      credentials: "include", // Always include cookies for auth
      signal: controller.signal,
    });

    // Handle non-JSON responses (e.g., 204 No Content)
    if (response.status === 204) {
      return {} as T;
    }

    // Handle 401 responses that might not have a JSON body
    if (response.status === 401) {
      throw new ApiRequestError(
        "Unauthorized",
        401
      );
    }

    // Parse response based on content type
    const contentType = response.headers.get('content-type');
    
    // Read response text
    const text = await response.text();
    
    if (!text || text.trim() === '') {
      // Empty response body
      if (response.ok) {
        return {} as T;
      }
      throw new ApiRequestError("Empty response from server", response.status);
    }

    // If response is plain text and successful, return it directly
    if (response.ok && contentType?.includes('text/plain')) {
      return text as T;
    }

    // Try to parse as JSON
    let data: ApiResponse<T>;
    try {
      data = JSON.parse(text);
    } catch {
      // If JSON parsing fails but response was successful, return text as-is
      if (response.ok) {
        return text as T;
      }
      // For error responses, try to provide useful info
      throw new ApiRequestError("Failed to parse error response", response.status);
    }

    // Handle error responses
    if (!response.ok) {
      throw new ApiRequestError(
        data.message || `Request failed with status ${response.status}`,
        response.status,
        data.errors
      );
    }

    // Return the data portion of the response
    // If the response has a data property, unwrap it, otherwise return the response directly
    return (data.data !== undefined ? data.data : data) as T;
  } catch (error) {
    // Re-throw ApiRequestError as-is
    if (error instanceof ApiRequestError) {
      throw error;
    }

    // Handle network errors and timeouts
    if (error instanceof TypeError) {
      throw new ApiRequestError(
        "Network error. Please check your connection.",
        0
      );
    }

    // Handle abort/timeout errors
    if (error instanceof Error && error.name === "AbortError") {
      throw new ApiRequestError(
        "Request timeout. The server is not responding.",
        0
      );
    }

    // Handle other errors
    throw new ApiRequestError(
      error instanceof Error ? error.message : "An unexpected error occurred",
      0
    );
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * API Client with convenience methods
 */
export const apiClient = {
  /**
   * Make a GET request
   * @param endpoint - API endpoint (e.g., "/users", "/lists/123")
   * @param options - Request options including query params
   */
  get: <T = unknown>(
    endpoint: string,
    options?: RequestOptions
  ): Promise<T> => {
    return request<T>(endpoint, {
      ...options,
      method: "GET",
    });
  },

  /**
   * Make a POST request
   * @param endpoint - API endpoint
   * @param body - Request body (will be JSON stringified unless it's FormData)
   * @param options - Additional request options
   */
  post: <T = unknown>(
    endpoint: string,
    body?: unknown,
    options?: RequestOptions
  ): Promise<T> => {
    return request<T>(endpoint, {
      ...options,
      method: "POST",
      body: body instanceof FormData ? body : (body ? JSON.stringify(body) : undefined),
    });
  },

  /**
   * Make a PUT request
   * @param endpoint - API endpoint
   * @param body - Request body (will be JSON stringified unless it's FormData)
   * @param options - Additional request options
   */
  put: <T = unknown>(
    endpoint: string,
    body?: unknown,
    options?: RequestOptions
  ): Promise<T> => {
    return request<T>(endpoint, {
      ...options,
      method: "PUT",
      body: body instanceof FormData ? body : (body ? JSON.stringify(body) : undefined),
    });
  },

  /**
   * Make a PATCH request
   * @param endpoint - API endpoint
   * @param body - Request body (will be JSON stringified unless it's FormData)
   * @param options - Additional request options
   */
  patch: <T = unknown>(
    endpoint: string,
    body?: unknown,
    options?: RequestOptions
  ): Promise<T> => {
    return request<T>(endpoint, {
      ...options,
      method: "PATCH",
      body: body instanceof FormData ? body : (body ? JSON.stringify(body) : undefined),
    });
  },

  /**
   * Make a DELETE request
   * @param endpoint - API endpoint
   * @param options - Request options
   */
  delete: <T = unknown>(
    endpoint: string,
    options?: RequestOptions
  ): Promise<T> => {
    return request<T>(endpoint, {
      ...options,
      method: "DELETE",
    });
  },
};

/**
 * Convenience export of the default API client
 */
export default apiClient;
